import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Award, MapPin, Phone, Mail, Globe, ArrowLeft, IdCard } from "lucide-react";
import { homesAgent } from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";

export default function AgentProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); setD(null); setErr(false); homesAgent(id).then(setD).catch(() => setErr(true)); }, [id]);

  if (err) return <Center><h1 className="text-3xl font-black text-[#0F172A]">Agent not found</h1><Link to="/homes/agents" className="mt-3 text-[#2563EB]">← All agents</Link></Center>;
  if (!d) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const a = d.agent;
  return (
    <div data-testid="agent-profile">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-12">
          <Link to="/homes/agents" className="mb-5 inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All agents</Link>
          <div className="flex flex-wrap items-center gap-6">
            {a.photo?.image_url ? <img src={a.photo.image_url} alt={a.name} className="h-28 w-28 rounded-2xl object-cover ring-2 ring-white/20" /> : <span className="flex h-28 w-28 items-center justify-center rounded-2xl bg-white/10 text-3xl font-black ring-2 ring-white/20">{a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#93B4FF]">{a.title}</span>
              <h1 className="text-4xl font-black md:text-5xl">{a.name}</h1>
              <div className="mt-1 text-white/70">{a.brokerage}</div>
              <div className="mt-2 flex items-center gap-1.5 text-sm text-white/70"><MapPin className="h-4 w-4 text-[#93B4FF]" /> {a.city_name}, {a.state_name} · <Award className="h-4 w-4 text-[#93B4FF]" /> {a.years_experience} yrs</div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-10 lg:grid-cols-[1fr_320px]">
        <div>
          <h2 className="text-2xl font-black text-[#0F172A]">About</h2>
          <p className="mt-2 leading-relaxed text-slate-600">{a.bio}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(a.specialties || []).map((s) => <span key={s} className="rounded-full bg-[#EEF2FF] px-3 py-1.5 text-sm font-medium text-[#2563EB]">{s}</span>)}
          </div>

          <h2 className="mt-10 text-2xl font-black text-[#0F172A]">Listings by {a.name.split(" ")[0]} ({d.listings.length})</h2>
          {d.listings.length ? (
            <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="agent-listings">
              {d.listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
            </div>
          ) : <p className="mt-3 text-slate-500">No active listings.</p>}
        </div>

        <aside>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="agent-contact">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Contact</div>
            <div className="space-y-2.5 text-sm">
              {a.phone && <div className="flex items-center gap-2 text-[#0F172A]"><Phone className="h-4 w-4 text-[#2563EB]" /> {a.phone}</div>}
              {a.email && <div className="flex items-center gap-2 break-all text-[#0F172A]"><Mail className="h-4 w-4 text-[#2563EB]" /> {a.email}</div>}
              <div className="flex items-center gap-2 text-[#0F172A]"><Globe className="h-4 w-4 text-[#2563EB]" /> {(a.languages || []).join(", ")}</div>
            </div>
            <button className="mt-4 w-full rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="agent-contact-btn">Request a call</button>
            <Link to={`/homes/card-studio?agent=${id}`} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-[#2563EB] px-4 py-2.5 text-sm font-bold text-[#2563EB] transition-colors hover:bg-[#EEF2FF]" data-testid="agent-card-studio-btn">
              <IdCard className="h-4 w-4" /> Create business card
            </Link>
            <p className="mt-2 text-[10px] text-slate-400">Sample profile — contact details are demonstration data.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
